import { TrashIcon } from "@xola/icons";
import { getSymbol, Input, Select, Table } from "@xola/ui-kit";
import { DemographicIcon, FormField } from "common";
import React, { ChangeEvent } from "react";
import { AdjustmentOperation, AdjustmentType, PartnerDemographicPrice } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";

interface DemographicPriceItemProps {
    agentDemographicPrice: PartnerDemographicPrice;
    index: number;
    setFieldValue: (key: string, value: any) => void;
    handleDelete: () => void;
}

export const DemographicPriceItem = ({
    agentDemographicPrice,
    index,
    setFieldValue,
    handleDelete,
}: DemographicPriceItemProps) => {
    const seller = useSeller();
    const discountPrice = agentDemographicPrice.discount;
    const commissionPrice = agentDemographicPrice.commission;

    const handleAdjustmentOperationChange = (value: AdjustmentOperation) => {
        if (value === AdjustmentOperation.FINAL) {
            setFieldValue(`${index}.discount.adjustmentType`, AdjustmentType.ABSOLUTE);
        }

        setFieldValue(`${index}.discount.adjustmentOperation`, value);
    };

    const demographicName = commissionPrice.catalogItem?.name;

    return (
        <Table.Row key={agentDemographicPrice.template.id} className="justify-between">
            <Table.Cell className="no-translate w-1/4">
                <FormField label={null} name={`${index}.name`}>
                    <div className="flex items-center">
                        <DemographicIcon className="mr-1" name={demographicName ?? ""} />
                        <div className="w-[200px]">
                            <AutoTooltipContainer tooltipContent={demographicName}>
                                {demographicName}
                            </AutoTooltipContainer>
                        </div>
                    </div>
                </FormField>
            </Table.Cell>

            <Table.Cell className="w-2/5">
                <FormField label={null} name={`${index}.adjustments`}>
                    <div className="flex">
                        <Input
                            className="h-9 w-[65px] rounded-r-none"
                            value={discountPrice.adjustmentAmount ? discountPrice.adjustmentAmount : null}
                            type="number"
                            min="0"
                            data-testid="demographic-terms-adjustment-input"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`${index}.discount.adjustmentAmount`, Number(e.target.value))
                            }
                        />

                        <Select
                            disabled={discountPrice.adjustmentOperation === AdjustmentOperation.FINAL}
                            value={discountPrice.adjustmentType}
                            className="w-12 rounded-none"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`${index}.discount.adjustmentType`, e.target.value)
                            }
                        >
                            <option value={AdjustmentType.ABSOLUTE} className="no-translate">
                                {getSymbol(seller.currency)}
                            </option>
                            <option value={AdjustmentType.PERCENT}>%</option>
                        </Select>

                        <Select
                            className="w-[110px] rounded-l-none"
                            value={discountPrice.adjustmentOperation}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handleAdjustmentOperationChange(e.target.value as AdjustmentOperation)
                            }
                        >
                            <option value={AdjustmentOperation.MARKUP}>Markup</option>
                            <option value={AdjustmentOperation.MARKDOWN}>Markdown</option>
                            <option value={AdjustmentOperation.DISCOUNT}>Discount</option>
                            <option value={AdjustmentOperation.FINAL}>Final Price</option>
                        </Select>
                    </div>
                </FormField>
            </Table.Cell>

            <Table.Cell className="w-[30%]">
                <FormField label={null} name={`${index}.commission`}>
                    <div className="flex">
                        <Input
                            className="h-9 w-[60px] rounded-r-none"
                            value={commissionPrice.adjustmentAmount ? commissionPrice.adjustmentAmount : null}
                            type="number"
                            min="0"
                            data-testid="demographic-terms-commission-input"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`${index}.commission.adjustmentAmount`, Number(e.target.value))
                            }
                        />

                        <Select
                            data-testid="demographic-terms-commission-input-algorithm"
                            value={commissionPrice.adjustmentType}
                            className="w-12 rounded-none"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`${index}.commission.adjustmentType`, e.target.value)
                            }
                        >
                            <option value={AdjustmentType.ABSOLUTE} className="no-translate">
                                {getSymbol(seller.currency)}
                            </option>
                            <option value={AdjustmentType.PERCENT}>%</option>
                        </Select>
                    </div>
                </FormField>
            </Table.Cell>

            <Table.Cell className="w-[5%]">
                <FormField label={null} name={`${index}.action`}>
                    <SecondaryButton size="small" onClick={handleDelete}>
                        <TrashIcon />
                    </SecondaryButton>
                </FormField>
            </Table.Cell>
        </Table.Row>
    );
};
